import { ApiNS } from 'app/api/api';
import { apiGetInvoiceById, apiGetInvoices } from 'app/api/invoices/invoices';
import { apiGetJobChanges } from 'app/api/job-changes/job-changes';
import axios from 'axios';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppNS } from '../';
import {
    apiAddNewPermit, apiCancelJob, apiChangeClientProjectOwner, apiGetProjectLead, apiSetLeadState, apiUpdatePaymentMethod, apiUpdateSiteContact
} from '../api/client-bookings/client-bookings';
import { AppThunk } from './index';

export interface ClientBookingsState {
  page: number;
  perPage: number;
  search: string;
  searchBy: string;
  projectType: string;
  totalEntries: number;
  items: Array<AppNS.ProjectLead>;
  currentLead: AppNS.ProjectLead | null;
  currentInvoice: AppNS.ProjectInvoice | null;
  isLeadLoading: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  isLeadLoadingError: boolean;
  isLeadStateLoadingError: boolean;
  isLeadStateLoading: boolean;

  isCancellingJob: boolean;
  isCancellingJobError: boolean;
  isJobCancelled: string;

  detailsActionDone: boolean;

  isAddingReviewError: boolean;
  isAddingReview: boolean;

  isAddingNewPermit: boolean;
  isAddingNewPermitError: string;

  isInvoiceLoaded: boolean;
  isInvoiceLoading: boolean;
  isInvoiceLoadingError: boolean;
  isChangingProjectOwner: boolean;
  isUpdatingContact: boolean;
  isUpdatingContactError: AppNS.Params | null;
  isUpdatingPaymentMethod: boolean;
  updatingPaymentMethodError: AppNS.Params | null;
}

const initialState: ClientBookingsState = {
  page: 1,
  perPage: 10,
  search: '',
  searchBy: '',
  projectType: '',
  currentLead: null,
  currentInvoice: null,
  totalEntries: 0,
  items: [],
  isLeadLoading: false,
  isLeadLoadingError: false,
  isLeadStateLoadingError: false,
  isLeadStateLoading: false,
  isLoaded: false,
  isLoading: false,
  isCancellingJob: false,
  isCancellingJobError: false,
  isJobCancelled: '',
  detailsActionDone: false,
  isAddingReviewError: false,
  isAddingReview: false,
  isAddingNewPermit: false,
  isAddingNewPermitError: '',
  isInvoiceLoaded: false,
  isInvoiceLoading: false,
  isInvoiceLoadingError: false,
  isChangingProjectOwner: false,
  isUpdatingContact: false,
  isUpdatingContactError: null,
  isUpdatingPaymentMethod: false,
  updatingPaymentMethodError: null,
};

const clientBookings = createSlice({
  name: 'clientBookings',
  initialState,
  reducers: {
    resetCollection(state) {
      state.items = [];
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setCurrentParams(state, action: PayloadAction<AppNS.Params>) {
      state.page = action.payload.page;
      state.search = action.payload.search;
      state.searchBy = action.payload.searchBy;
      state.projectType = action.payload.projectType;
    },
    getLeadStart(state: ClientBookingsState) {
      state.currentLead = null;
      state.isLoaded = false;
      state.isLeadLoading = true;
      state.isLeadLoadingError = false;
    },
    getCurrentLeadSuccess(state: ClientBookingsState, action: PayloadAction<AppNS.ProjectLead>) {
      state.isLeadLoadingError = false;
      state.isLeadLoading = false;
      state.currentLead = action.payload;
    },
    getLeadFailed(state: ClientBookingsState) {
      state.isLeadLoadingError = true;
      state.isLeadLoading = false;
    },
    setLeadStateStart(state: ClientBookingsState) {
      state.isLeadStateLoading = true;
    },
    setLeadStateSuccess(state: ClientBookingsState, action: PayloadAction<AppNS.ProjectLeadState>) {
      state.isLeadStateLoading = false;
      if (state.currentLead) {
        state.currentLead.state = action.payload;
      }
    },
    setLeadStateFailed(state: ClientBookingsState) {
      state.isLeadStateLoadingError = true;
      state.isLeadStateLoading = false;
    },
    cancelJobStart(state: ClientBookingsState) {
      state.isCancellingJob = true;
    },
    cancelJobSuccess(state: ClientBookingsState, action: PayloadAction<{ id: string; requestOnly: boolean }>) {
      state.isCancellingJob = false;
      if (action.payload.requestOnly) {
        if (state.currentLead?.job) {
          state.currentLead.job.cancellationMessage = 'cancelled';
        }
      } else {
        state.isJobCancelled = action.payload.id;
        if (state.currentLead) {
          state.currentLead.state = 'cancelled_by_client';
        }
        if (state.items.length) {
          state.items = state.items.map((item) => {
            if (item.id === action.payload.id) {
              return { ...item, isDeleted: true };
            }
            return item;
          });
        }
      }
    },
    cancelJobFailed(state: ClientBookingsState) {
      state.isCancellingJobError = true;
      state.isCancellingJob = false;
    },
    setDetailsActionDone(state: ClientBookingsState, action: PayloadAction<boolean>) {
      state.detailsActionDone = action.payload;
    },
    setJobCancelled(state: ClientBookingsState, action: PayloadAction<string>) {
      state.isJobCancelled = action.payload;
    },
    resetCurrentLead(state: ClientBookingsState) {
      state.currentLead = null;
    },

    addNewPermitStart(state: ClientBookingsState) {
      state.isAddingNewPermit = true;
      state.isAddingNewPermitError = '';
    },

    changeProjectOwnerFailed(state: ClientBookingsState) {
      state.isChangingProjectOwner = false;
    },
    changeProjectOwnerStart(state: ClientBookingsState) {
      state.isChangingProjectOwner = true;
    },
    changeProjectOwnerSuccess(state: ClientBookingsState, action: PayloadAction<AppNS.Params>) {
      state.isChangingProjectOwner = false;
      if (state.currentLead && state.currentLead.job && state.currentLead.job.client) {
        state.currentLead.job.client.id = action.payload.id;
        state.currentLead.job.client.firstName = action.payload.firstName;
        state.currentLead.job.client.lastName = action.payload.lastName;
      }
    },
    changeAgencyProjectOwnerSuccess(state: ClientBookingsState, action: PayloadAction<AppNS.Params>) {
      state.isChangingProjectOwner = false;
      if (state.currentLead && state.currentLead.job && state.currentLead.job.agency) {
        state.currentLead.job.agency.id = action.payload.id;
        state.currentLead.job.agency.firstName = action.payload.firstName;
        state.currentLead.job.agency.lastName = action.payload.lastName;
      }
    },
    addNewPermitSuccess(state: ClientBookingsState, action: PayloadAction<{ permit: AppNS.PickedPermit }>) {
      state.isAddingNewPermitError = '';
      state.isAddingNewPermit = false;

      if (state.currentLead && state.currentLead.job?.jobChanges) {
        state.currentLead.job?.jobChanges.push({
          id: String(new Date().getTime()),
          state: 'pending',
          permitNumber: action.payload.permit.id,
          permit: {
            id: action.payload.permit.id,
            wallCheckComplete: action.payload.permit.wallCheckComplete,
            wallCheckRequired: action.payload.permit.wallCheckRequired,
            inspections: action.payload.permit.inspections.map((item) => {
              return {
                id: item,
                status: 'pending',
                name: item,
                dcraState: 'pending',
                dcraStateRejectionComment: '',
              };
            }),
            name: '',
            owner: '',
            scope: action.payload.permit.scope || '',
            addressInfo: '',
            permitNumber: action.payload.permit.id,
            inspectionGroup: '',
            hasStopWorkOrder: false,
            hasTPIA: false,
            hasRecInsps: false,
            permitUsed: '',
            swoConditions: [],
          },
        });
      }
    },
    addNewPermitFailed(state: ClientBookingsState, action: PayloadAction<string>) {
      state.isAddingNewPermitError = action.payload;
      state.isAddingNewPermit = false;
    },
    resetAddingNewPermitError(state: ClientBookingsState) {
      state.isAddingNewPermitError = '';
    },
    getInvoiceStart(state: ClientBookingsState) {
      state.currentInvoice = null;
      state.isInvoiceLoaded = false;
      state.isInvoiceLoading = true;
      state.isInvoiceLoadingError = false;
    },
    getCurrentInvoiceSuccess(state: ClientBookingsState, action: PayloadAction<AppNS.ProjectInvoice>) {
      state.isInvoiceLoadingError = false;
      state.isInvoiceLoading = false;
      state.currentInvoice = action.payload;
    },
    setUpdateContactStart(state: ClientBookingsState) {
      state.isUpdatingContact = true;
      state.isUpdatingContactError = null;
    },
    setUpdateContactSuccess(state: ClientBookingsState, action: PayloadAction<AppNS.Params>) {
      state.isUpdatingContact = false;
      if (state.currentLead && state.currentLead.job) {
        state.currentLead.job = {
          ...state.currentLead.job,
          contactEmail: action.payload.properties.siteContactEmail,
          contactPhone: action.payload.properties.siteContactPhone,
          contactName: action.payload.properties.siteContactName,
        };
      }
    },
    updateContactFailure(state: ClientBookingsState, action: PayloadAction<AppNS.Params>) {
      state.isUpdatingContact = false;
      state.isUpdatingContactError = action.payload;
    },
    updatePaymentMethodStart(state: ClientBookingsState) {
      state.isUpdatingPaymentMethod = true;
      state.updatingPaymentMethodError = null;
    },
    updatePaymentMethodSuccess(state: ClientBookingsState, action: PayloadAction<AppNS.Params>) {
      state.isUpdatingPaymentMethod = false;
      state.updatingPaymentMethodError = null;
      if (state.currentLead) {
        state.currentLead.paymentMethod = action.payload.properties.paymentMethod;
      }
    },
    updatePaymentMethodFailed(state: ClientBookingsState, action: PayloadAction<AppNS.Params>) {
      state.isUpdatingPaymentMethod = false;
      state.updatingPaymentMethodError = action.payload;
    },
    setInvoicesforCurrentLead(state: ClientBookingsState, action: PayloadAction<ApiNS.InvoicesListResponse>) {
      if (state.currentLead) {
        state.currentLead.invoices = action.payload.results as AppNS.ProjectInvoice[];
      }
    },
    setJobChangesforCurrentLead(state: ClientBookingsState, action: PayloadAction<AppNS.Params[]>) {
      if (state.currentLead?.job) {
        state.currentLead.job.jobChanges = action.payload as AppNS.JobChange[];
      }
    },
    setCurrentBookingSwoState(state: ClientBookingsState, action: PayloadAction<AppNS.Project>) {
      if (state.currentLead?.job?.id === action.payload.id) {
        state.currentLead.job.swoSetAt = action.payload.swoSetAt;
      }
    },
  },
});

export const {
  cancelJobFailed,
  cancelJobStart,
  cancelJobSuccess,
  getCurrentLeadSuccess,
  getLeadFailed,
  getLeadStart,
  setCurrentParams,
  setDetailsActionDone,
  setLeadStateFailed,
  setLeadStateStart,
  setLeadStateSuccess,
  setJobCancelled,
  resetCurrentLead,
  addNewPermitStart,
  addNewPermitSuccess,
  addNewPermitFailed,
  getInvoiceStart,
  resetAddingNewPermitError,
  getCurrentInvoiceSuccess,
  changeProjectOwnerStart,
  changeProjectOwnerSuccess,
  changeProjectOwnerFailed,
  changeAgencyProjectOwnerSuccess,
  setUpdateContactStart,
  setUpdateContactSuccess,
  updateContactFailure,
  updatePaymentMethodStart,
  updatePaymentMethodSuccess,
  updatePaymentMethodFailed,
  setInvoicesforCurrentLead,
  setJobChangesforCurrentLead,
  setCurrentBookingSwoState,
} = clientBookings.actions;

export default clientBookings.reducer;

export const getCurrentLead =
  (type: string, id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getLeadStart());
      const result = await apiGetProjectLead(type, id);

      dispatch(getCurrentLeadSuccess(result));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(getLeadFailed());
    }
  };

export const setLeadState =
  (id: string, state: AppNS.ProjectLeadState): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLeadStateStart());
      const result = await apiSetLeadState({
        id,
        properties: {
          state: state,
        },
      });
      dispatch(setLeadStateSuccess(result));
      dispatch(setDetailsActionDone(true));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(setLeadStateFailed());
    }
  };

export const updateSiteContact =
  (id: string, properties: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setUpdateContactStart());
      const result = await apiUpdateSiteContact(id, {
        siteContactName: properties.contactName,
        siteContactPhone: properties.contactPhone,
        siteContactEmail: properties.contactEmail,
      });
      dispatch(setUpdateContactSuccess(result));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      if (err) {
        dispatch(updateContactFailure(err));
      }
    }
  };

export const cancelJob =
  (id: string, message: string, requestOnly: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(cancelJobStart());
      await apiCancelJob({ id, message }, requestOnly);
      dispatch(cancelJobSuccess({ id, requestOnly }));
      if (!requestOnly) {
        dispatch(setDetailsActionDone(true));
      }
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(cancelJobFailed());
    }
  };

export const addNewPermit =
  (id: string, permit: AppNS.PickedPermit): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(addNewPermitStart());
      await apiAddNewPermit(id, permit);
      dispatch(addNewPermitSuccess({ permit }));
    } catch (err: any) {
      if (err) {
        dispatch(addNewPermitFailed(err as string));
      }
    }
  };

export const getCurrentInvoice =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getInvoiceStart());
      const result = await apiGetInvoiceById(id);

      dispatch(getCurrentInvoiceSuccess(result));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(getLeadFailed());
    }
  };

export const changeClientProjectOwner =
  (id: string, clientId: string, pm: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(changeProjectOwnerStart());
      await apiChangeClientProjectOwner(id, clientId);
      dispatch(changeProjectOwnerSuccess(pm));
    } catch (err: any) {
      if (err) {
        dispatch(changeProjectOwnerFailed());
      }
    }
  };

export const updatePaymentMethod =
  (data: { id: string; paymentMethodId: string }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updatePaymentMethodStart());
      const result = await apiUpdatePaymentMethod({
        id: data.id,
        properties: {
          payment_method_id: data.paymentMethodId,
        },
      });

      dispatch(updatePaymentMethodSuccess(result));
    } catch (err: any) {
      if (err) {
        dispatch(updatePaymentMethodFailed(err));
      }
    }
  };

export const getInvoicesForCurrentBooking =
  (bookingId: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await apiGetInvoices(bookingId, '', '', 1, 'booking_id', '');
      dispatch(setInvoicesforCurrentLead(response));
    } catch (err: any) {}
  };

export const getJobChangesForCurrentBooking =
  (jobId: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await apiGetJobChanges(jobId);
      dispatch(setJobChangesforCurrentLead(response));
    } catch (err: any) {}
  };
